'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { mentionRegistry, type MentionContext, type MentionKind, type ResolvedMention } from '@/lib/mentions'

type MentionPickerProps = {
  query: string
  open: boolean
  selectedIndex: number
  onSelectedIndexChange: (index: number) => void
  onSelect: (mention: ResolvedMention) => void
  /** Notified whenever the candidate list changes so the host can wire keyboard confirm. */
  onCandidatesChange?: (candidates: ResolvedMention[]) => void
  mentionContext: MentionContext
}

// Display order + labels for grouped sections. Kinds not in this map
// fall through to a Title-Cased version of the kind string.
const KIND_ORDER: MentionKind[] = ['campaign', 'video', 'brand']
const KIND_LABELS: Record<string, string> = {
  campaign: 'Campaigns',
  video: 'Videos',
  brand: 'Brands',
}

function labelForKind(kind: string): string {
  return KIND_LABELS[kind] ?? kind.charAt(0).toUpperCase() + kind.slice(1)
}

export function MentionPicker({
  query,
  open,
  selectedIndex,
  onSelectedIndexChange,
  onSelect,
  onCandidatesChange,
  mentionContext,
}: MentionPickerProps) {
  const [candidates, setCandidates] = useState<ResolvedMention[]>([])
  // `pending` flips while a search is in flight, but we keep showing the
  // previous candidate list. Previously `loading` swapped the entire panel
  // content for a "Searching…" placeholder on every keypress, so the rows
  // visibly cleared and flickered.
  const [pending, setPending] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  useEffect(() => {
    if (!open) {
      // Picker closed — reset everything so the next open starts clean.
      if (candidates.length !== 0) {
        setCandidates([])
        onCandidatesChange?.([])
      }
      setHasSearched(false)
      setPending(false)
      return
    }
    let cancelled = false
    setPending(true)

    mentionRegistry.search(query, mentionContext).then(groups => {
      if (cancelled) return
      // Flatten in our preferred display order (campaign → video → brand)
      // so keyboard navigation matches what the user sees on screen.
      // Unknown kinds get appended after the known set in insertion
      // order so they don't get silently dropped if/when we add more
      // kinds in a future PR.
      const byKind = new Map<string, ResolvedMention[]>()
      for (const g of groups) {
        if (!byKind.has(g.kind)) byKind.set(g.kind, [])
        byKind.get(g.kind)!.push(...g.candidates)
      }
      const orderedKinds: string[] = [
        ...KIND_ORDER.filter((k) => byKind.has(k)),
        ...Array.from(byKind.keys()).filter((k) => !KIND_ORDER.includes(k as MentionKind)),
      ]
      const all: ResolvedMention[] = []
      for (const k of orderedKinds) {
        all.push(...(byKind.get(k) ?? []))
      }
      setCandidates(all)
      onCandidatesChange?.(all)
      setHasSearched(true)
      setPending(false)
    }).catch(() => {
      if (!cancelled) {
        // Don't blank the previous list on transient errors — let the user
        // keep navigating what they had until a successful response replaces it.
        setHasSearched(true)
        setPending(false)
      }
    })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, query, mentionContext])

  useEffect(() => {
    if (selectedIndex >= candidates.length && candidates.length > 0) {
      onSelectedIndexChange(candidates.length - 1)
    }
  }, [candidates.length, onSelectedIndexChange, selectedIndex])

  // Auto-scroll the keyboard-selected row into view WITHIN the picker's own
  // scroll container. Previously this used `el.scrollIntoView({ block:
  // 'nearest' })`, which can bubble up to ancestor scroll containers and
  // ends up scrolling the page (or the chat panel) when the picker is
  // already fully visible. Compute the offsets manually against the
  // nearest scrollable ancestor so only the picker scrolls.
  const rowRefs = useRef<Array<HTMLButtonElement | null>>([])
  useEffect(() => {
    const el = rowRefs.current[selectedIndex]
    if (!el) return
    // Walk up until we find the picker's overflow-y-auto container.
    let scrollParent: HTMLElement | null = el.parentElement
    while (scrollParent) {
      const style = window.getComputedStyle(scrollParent)
      const overflowY = style.overflowY
      if (overflowY === 'auto' || overflowY === 'scroll') break
      scrollParent = scrollParent.parentElement
    }
    if (!scrollParent) return
    const rowRect = el.getBoundingClientRect()
    const parentRect = scrollParent.getBoundingClientRect()
    if (rowRect.top < parentRect.top) {
      scrollParent.scrollTop -= parentRect.top - rowRect.top
    } else if (rowRect.bottom > parentRect.bottom) {
      scrollParent.scrollTop += rowRect.bottom - parentRect.bottom
    }
  }, [selectedIndex])

  // Group candidates by kind for sectioned rendering. Each section
  // tracks its global index range so a single section-by-section render
  // can still wire up the flat `selectedIndex` for keyboard nav.
  const sections = useMemo(() => {
    const acc: Array<{ kind: string; label: string; items: Array<{ candidate: ResolvedMention; index: number }> }> = []
    let cursor = 0
    let currentKind: string | null = null
    let currentEntry: (typeof acc)[number] | null = null
    for (const candidate of candidates) {
      if (candidate.kind !== currentKind) {
        currentKind = candidate.kind
        currentEntry = { kind: candidate.kind, label: labelForKind(candidate.kind), items: [] }
        acc.push(currentEntry)
      }
      currentEntry!.items.push({ candidate, index: cursor })
      cursor++
    }
    return acc
  }, [candidates])

  if (!open) return null

  const showInitialSearching = pending && !hasSearched
  const showEmpty = !pending && hasSearched && candidates.length === 0

  // Shared shell for all three states (loading, empty, populated). Width
  // matches the prompt box (no `max-w-xl` cap) and colors use atrium-*
  // tokens so light/dark both render via globals.css CSS variables.
  const shellCls =
    'absolute bottom-full left-0 z-50 mb-2 w-full overflow-hidden rounded-xl border border-atrium-border bg-atrium-inner shadow-lg ring-1 ring-atrium-black/5 dark:ring-black/40'

  if (showInitialSearching) {
    return (
      <div className={shellCls}>
        <div className="px-4 py-3 text-xs text-atrium-muted">Searching…</div>
      </div>
    )
  }

  if (showEmpty) {
    return (
      <div className={shellCls}>
        <div className="px-4 py-3 text-xs text-atrium-muted">
          {query ? `No results for "@${query}"` : 'No results'}
        </div>
      </div>
    )
  }

  return (
    <div className={shellCls}>
      <div className="max-h-72 overflow-y-auto p-1">
        {pending ? (
          <div className="flex items-center justify-end px-2 pb-1 pt-1">
            <span className="text-[10px] text-atrium-muted">Searching…</span>
          </div>
        ) : null}
        {sections.map((section, sectionIdx) => (
          <div key={section.kind} className={sectionIdx > 0 ? 'mt-1 border-t border-atrium-border pt-1' : ''}>
            <div className="px-2 pb-1 pt-1">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-atrium-muted">
                {section.label}
              </span>
            </div>
            {section.items.map(({ candidate, index }) => {
              const selected = index === selectedIndex
              return (
                <button
                  key={`${candidate.kind}:${candidate.id}`}
                  ref={(el) => { rowRefs.current[index] = el }}
                  type="button"
                  onMouseEnter={() => onSelectedIndexChange(index)}
                  onClick={() => onSelect(candidate)}
                  className={cn(
                    'flex w-full min-w-0 items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors',
                    selected ? 'bg-atrium-content text-atrium-black' : 'text-atrium-black hover:bg-atrium-content/70'
                  )}
                >
                  <span className="text-atrium-muted shrink-0 text-sm">@</span>
                  <span className="truncate text-sm font-medium">{candidate.display}</span>
                  {candidate.subtitle ? (
                    <span className="ml-auto truncate text-xs text-atrium-muted shrink-0 max-w-[40%]">{candidate.subtitle}</span>
                  ) : null}
                </button>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
